import { z } from 'zod';

import { ExpandableErrorAlert } from '@/components/ExpandableErrorAlert';
import {
  EPromptOptimizationLogType,
  PromptOptimizationLog,
} from '@/types/schema';

import { DatasetNameSchema, DemoSchema, TaskDescriptionSchema } from './types';
import { formatDate } from './utils';

const LogError = ({ log }: { log: PromptOptimizationLog }) => (
  <>
    <div className="font-mono text-xs text-red-500">
      {formatDate(log.created_at)}
    </div>
    <ExpandableErrorAlert error={log.message} />
  </>
);

const LogInfo = ({ log }: { log: PromptOptimizationLog }) => {
  const { data, message, created_at } = log;

  return (
    <>
      <div className="font-mono text-xs">{formatDate(created_at)}</div>
      <div className="flex flex-col gap-1 font-mono">
        <div className="flex flex-row">{message}</div>
        {data && (
          <>
            {TaskDescriptionSchema.safeParse(data).success && (
              <div className="text-sm text-gray-600">
                {
                  (data as z.infer<typeof TaskDescriptionSchema>)
                    .task_description
                }
              </div>
            )}
            {DatasetNameSchema.safeParse(data).success && (
              <div className="text-sm text-gray-600">
                {(data as z.infer<typeof DatasetNameSchema>).dataset_name}
              </div>
            )}
            {DemoSchema.safeParse(data).success && (
              <div className="text-sm text-gray-600">
                {(data as z.infer<typeof DemoSchema>).name}
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
};

const LogResult = ({ log }: { log: PromptOptimizationLog }) => {
  return (
    <>
      <div className="font-mono text-xs text-yellow-600">
        {formatDate(log.created_at)}
      </div>
      <div className="flex flex-row gap-2">
        <div className="font-mono">{log.message}</div>
        <a href={log.data?.link} className="font-mono text-blue-500 underline">
          {log.data?.name}
        </a>
      </div>
    </>
  );
};

const LogWrapper = ({ children }: { children: React.ReactNode }) => {
  return <div className="grid grid-cols-[1fr_5fr] gap-2">{children}</div>;
};

export const LogItem = ({ log }: { log: PromptOptimizationLog }) => {
  const content = (() => {
    if (log.log_type === EPromptOptimizationLogType.ERROR) {
      return <LogError log={log} />;
    } else if (log.log_type === EPromptOptimizationLogType.INFO) {
      return <LogInfo log={log} />;
    } else if (log.log_type === EPromptOptimizationLogType.RESULT) {
      return <LogResult log={log} />;
    }
  })();

  return <LogWrapper>{content}</LogWrapper>;
};
